import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useSellerContacts } from '~/api/sellerContacts';
import { SelectContactsModal } from '~/components/common/SelectContactsModal';
import { sellerLabel, toastGraphQLError } from '~/utils';
import type { RecipientOrganization } from '~/utils/contact';

export type SellerContactsOwner = Readonly<{
  sellingOrganization: RecipientOrganization;
  primaryContact?: { id: string } | null;
  recipients: ReadonlyArray<{ id: string }>;
  lockedRecipientIds: string[];
}>;

type SellerContactsModalProps = Readonly<{
  owner: SellerContactsOwner;
  subtitle: string;
  lockedLabel: string;
  save: (
    recipientUserIds: string[],
    primaryContactUserId: string
  ) => Promise<unknown>;
  isSaving: boolean;
  onClose: () => void;
}>;

export function SellerContactsModal({
  owner,
  subtitle,
  lockedLabel,
  save,
  isSaving,
  onClose,
}: SellerContactsModalProps) {
  const { t } = useTranslation('general');
  const { contacts, status } = useSellerContacts(owner.sellingOrganization.id);

  const handleConfirm = async (
    recipientUserIds: string[],
    primaryContactUserId: string
  ) => {
    try {
      await save(recipientUserIds, primaryContactUserId);
      toast.success(t('select_contacts_modal.saved'));
      onClose();
    } catch (error) {
      toastGraphQLError(error);
      onClose();
    }
  };

  return (
    <SelectContactsModal
      isOpen
      onClose={onClose}
      title={t('select_contacts_modal.title', {
        sellerName: sellerLabel(owner.sellingOrganization),
      })}
      subtitle={subtitle}
      lockedLabel={lockedLabel}
      primaryRequiredMessage={t('select_contacts_modal.primary_required')}
      organizationId={owner.sellingOrganization.id}
      contacts={contacts}
      contactsStatus={status}
      initialContactIds={owner.recipients.map((recipient) => recipient.id)}
      lockedContactIds={owner.lockedRecipientIds}
      initialPrimaryContactId={
        owner.primaryContact ? owner.primaryContact.id : undefined
      }
      openAddFormWhenEmpty
      onConfirm={(recipientUserIds, primaryContact) =>
        void handleConfirm(recipientUserIds, primaryContact.id)
      }
      isSubmitting={isSaving}
    />
  );
}
